"use client";

import { createContext, useContext, type ReactNode } from "react";
import {
  useConfirmWarnings,
  type ConfirmWarningInput,
} from "../hooks/useConfirmWarnings";
import { ConfirmWarningsDialog } from "../components/dialogs/ConfirmWarningsDialog";

/**
 * The single confirm-warning gate shared across the recap workspace. Replaces
 * the old ZeroQuantity / ZeroPrice / RequiredFields providers — one provider,
 * one dialog, one aggregated check.
 */
type ConfirmWarningsFn = (
  input: ConfirmWarningInput,
  action: () => void,
) => void;

const ConfirmWarningsContext = createContext<ConfirmWarningsFn | null>(null);

export function ConfirmWarningsProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const { checkAndConfirm, dialogProps } = useConfirmWarnings();

  return (
    <ConfirmWarningsContext.Provider value={checkAndConfirm}>
      {children}
      <ConfirmWarningsDialog {...dialogProps} />
    </ConfirmWarningsContext.Provider>
  );
}

/**
 * Returns the shared confirm-warning gate from the nearest provider, or null
 * when outside RecapProvider (e.g. PreOrderContent in chat).
 */
export function useConfirmWarningsCheck(): ConfirmWarningsFn | null {
  return useContext(ConfirmWarningsContext);
}
